/**
 * The mentor's weekly working hours.
 *
 * *** THE TENANT'S WEEK IS A CEILING ***
 * `operating_days` says which days the institute runs at all. Both must
 * allow a day for slots to exist, so a day the institute is closed is shown
 * as closed rather than as an editable row that silently produces nothing.
 */

import { useEffect, useState } from 'react'
import { Card } from '@/components/ui/Page'
import { Button } from '@/components/ui/Button'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import { useSetMyWorkingHours } from '@/lib/queries'
import type { WorkingHours, WorkingHoursEntry } from '@/types/api'
import styles from './AvailabilityPage.module.css'

/* 0 = Monday, matching the server. */
const DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]

const DEFAULT_START = '09:00'
const DEFAULT_END = '17:00'

/** "09:00:00" or "09:00" → "09:00", for an `<input type="time">`. */
function hhmm(value: string): string {
  return value.slice(0, 5)
}

interface Row {
  day_of_week: number
  start_time: string
  end_time: string
  is_enabled: boolean
}

export function WorkingHoursSection({
  data,
  loading,
}: {
  data: WorkingHours | null
  loading: boolean
}) {
  const toast = useToast()
  const save = useSetMyWorkingHours()
  const [rows, setRows] = useState<Row[]>([])

  /*
    Seven rows always, whether or not the server holds one for each day —
    a missing row IS the "not working" state, and rendering only what
    exists would leave a mentor no way to add a day.
  */
  useEffect(() => {
    if (!data) return
    setRows(
      DAYS.map((_, day) => {
        const found = data.entries.find((e) => e.day_of_week === day)
        return {
          day_of_week: day,
          start_time: hhmm(found?.start_time ?? DEFAULT_START),
          end_time: hhmm(found?.end_time ?? DEFAULT_END),
          is_enabled: found?.is_enabled ?? false,
        }
      }),
    )
  }, [data])

  if (loading) return <p className={styles.muted}>Loading your hours…</p>
  if (!data) return null

  const operating = new Set(data.operating_days)

  const update = (day: number, patch: Partial<Row>) => {
    setRows((prev) =>
      prev.map((r) => (r.day_of_week === day ? { ...r, ...patch } : r)),
    )
  }

  const submit = async () => {
    /*
      *** SEND ONLY ENABLED DAYS ***
      The server stores a row per working day; an entry flagged disabled
      would be a row that exists and does nothing. Days the TENANT is
      closed are dropped too — saving them is how a mentor ends up with
      hours that generate no slots and no explanation.
    */
    const entries: WorkingHoursEntry[] = rows
      .filter((r) => r.is_enabled && operating.has(r.day_of_week))
      .map((r) => ({
        day_of_week: r.day_of_week,
        start_time: `${r.start_time}:00`,
        end_time: `${r.end_time}:00`,
        is_enabled: true,
      }))

    const bad = entries.find((e) => e.start_time >= e.end_time)
    if (bad) {
      toast.error(
        `${DAYS[bad.day_of_week]}: the finish time must be after the start.`,
      )
      return
    }

    try {
      await save.mutateAsync(entries)
      toast.success('Hours saved.')
    } catch (caught) {
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not save your hours.',
      )
    }
  }

  const workingCount = rows.filter(
    (r) => r.is_enabled && operating.has(r.day_of_week),
  ).length

  return (
    <Card>
      <p className={styles.sectionNote}>
        Times are in your own timezone ({data.timezone}). Students see them
        converted to theirs.
      </p>

      <div className={styles.days}>
        {rows.map((row) => {
          const closed = !operating.has(row.day_of_week)
          return (
            <div
              key={row.day_of_week}
              className={`${styles.day} ${closed ? styles.dayClosed : ''}`}
            >
              <label className={styles.dayToggle}>
                <input
                  type="checkbox"
                  checked={row.is_enabled && !closed}
                  disabled={closed}
                  onChange={(e) =>
                    update(row.day_of_week, { is_enabled: e.target.checked })
                  }
                />
                <span className={styles.dayName}>{DAYS[row.day_of_week]}</span>
              </label>

              {closed ? (
                /*
                  Not an editable row. The institute does not operate this
                  day, so any hours here would produce no slots — and a
                  mentor would have no way to find out why.
                */
                <span className={styles.dayNote}>
                  Your institute is closed
                </span>
              ) : row.is_enabled ? (
                <div className={styles.times}>
                  <input
                    type="time"
                    className={styles.time}
                    value={row.start_time}
                    onChange={(e) =>
                      update(row.day_of_week, { start_time: e.target.value })
                    }
                    aria-label={`${DAYS[row.day_of_week]} start`}
                  />
                  <span className={styles.toLabel}>to</span>
                  <input
                    type="time"
                    className={styles.time}
                    value={row.end_time}
                    onChange={(e) =>
                      update(row.day_of_week, { end_time: e.target.value })
                    }
                    aria-label={`${DAYS[row.day_of_week]} finish`}
                  />
                </div>
              ) : (
                <span className={styles.dayNote}>Not working</span>
              )}
            </div>
          )
        })}
      </div>

      <div className={styles.actions}>
        <Button variant="primary" onClick={submit} loading={save.isPending}>
          Save hours
        </Button>
        {/*
          *** NO HOURS MEANS NO BOOKINGS ***
          Saving an empty week is allowed — a mentor may be stepping away —
          but it must not be a silent outcome.
        */}
        {workingCount === 0 && (
          <span className={styles.warnNote}>
            With no working days, students cannot book you at all.
          </span>
        )}
      </div>
    </Card>
  )
}
